import React from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { motion } from 'framer-motion';
import { Loader2, Calendar, Search, Newspaper, ArrowRight, Sparkles, Star, Diamond, Gem, Crown, Wrench, Server, Code, HardDrive, Shield, Award, Zap, Cloud } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { PublicLayout } from '../../components/layout/PublicLayout';
import { fetchBlogPosts, fetchBlogCategories, type BlogPost, type BlogCategory } from '../../services/blogService';
import { useQuery } from '@tanstack/react-query';
import { SEOMetaTags } from '../../components/shared/SEOMetaTags';
import { useBlogStore } from '../../stores/blogStore';
import { useTenantContext } from '../../context/TenantContext';

// Mapping d'icônes pour personnalisation
const iconMap: Record<string, React.ElementType> = {
  Newspaper,
  Sparkles,
  Star,
  Diamond,
  Gem,
  Crown,
  Wrench,
  Server,
  Code,
  HardDrive,
  Shield,
  Award,
  Zap,
  Cloud,
};

function hexToRgba(hex: string, alpha = 1) {
  const cleaned = hex.replace('#', '');
  const bigint = parseInt(cleaned.length === 3
    ? cleaned.split('').map((c) => c + c).join('')
    : cleaned, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

// Les animations sont excellentes et sont conservées
const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
};

const cardVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } }
};

export const BlogHome = () => {
  // La logique du store Zustand est parfaite et est conservée
  const { selectedCategory, searchQuery, setBlogCategory, setSearchQuery, resetFilters } = useBlogStore();
  const { tenant, previewConfig } = useTenantContext();

  // Configuration de l'en-tête du blog
  const blogHeaderConfig = React.useMemo(() => {
    const config = (previewConfig?.pages?.blog?.header) ?? (tenant?.config?.pages?.blog?.header) ?? {};
    return {
      badgeLabel: config.badgeLabel || 'Blog ECATHEBA',
      title: config.title || 'Réflexions théologiques',
      line1: config.line1 || 'Découvrez nos articles, réflexions spirituelles et actualités théologiques.',
      line2: config.line2 || 'Approfondissez votre foi et votre compréhension',
    };
  }, [tenant?.config?.pages?.blog?.header, previewConfig?.pages?.blog?.header]);

  // Thème Blog (preview > tenant)
  const blogTheme = React.useMemo(() => {
    const base = (previewConfig?.theme?.notFound) ?? (tenant?.config?.theme?.notFound) ?? ({} as any);
    const themeColors = {
      c1: (tenant?.config?.color1 as string) || '#fe8d17', // primaire (bouton)
      c2: (tenant?.config?.color2 as string) || '#008aef', // secondaire (bouton)
      c3: (tenant?.config?.color3 as string) || '#ffffff',
      c4: (tenant?.config?.color4 as string) || '#9ca3af',
    };
    const defaults = {
      overlay: {
        enabled: true,
        useGradient: true,
        orientation: 'to-b', // to-b, to-t, to-r, to-l, to-tr, to-br, to-tl, to-bl
        color1: '#08062b',
        color2: '#08062b',
        color3: '#08062b',
        opacity1: 0.95,
        opacity2: 0.80,
        opacity3: 0.95,
      },
      icons: [
        { enabled: true, icon: 'Newspaper', color: themeColors.c1, size: 32 }, // top-left
        { enabled: true, icon: 'Sparkles',   color: themeColors.c2, size: 28 }, // top-right
        { enabled: true, icon: 'Diamond',    color: themeColors.c2, size: 24 }, // bottom-left
        { enabled: true, icon: 'Star',       color: themeColors.c1, size: 30 }, // bottom-right
      ]
    } as any;
    return {
      overlay: {
        enabled: base.overlay?.enabled ?? defaults.overlay.enabled,
        useGradient: base.overlay?.useGradient ?? defaults.overlay.useGradient,
        orientation: base.overlay?.orientation ?? defaults.overlay.orientation,
        color1: base.overlay?.color1 ?? defaults.overlay.color1,
        color2: base.overlay?.color2 ?? defaults.overlay.color2,
        color3: base.overlay?.color3 ?? defaults.overlay.color3,
        opacity1: base.overlay?.opacity1 ?? defaults.overlay.opacity1,
        opacity2: base.overlay?.opacity2 ?? defaults.overlay.opacity2,
        opacity3: base.overlay?.opacity3 ?? defaults.overlay.opacity3,
      },
      icons: (base.icons && Array.isArray(base.icons) && base.icons.length === 4)
        ? base.icons.map((i: any, idx: number) => ({
            enabled: i?.enabled ?? defaults.icons[idx].enabled,
            icon: i?.icon ?? defaults.icons[idx].icon,
            color: i?.color ?? defaults.icons[idx].color,
            size: Number(i?.size ?? defaults.icons[idx].size),
          }))
        : defaults.icons,
    };
  }, [tenant, previewConfig]);

  // La gestion des données avec React Query est excellente et est conservée
  const { data: allCategories, isLoading: categoriesLoading } = useQuery({
    queryKey: ['blogCategories'],
    queryFn: fetchBlogCategories
  });

  const { data: posts, isLoading: postsLoading } = useQuery({
    queryKey: ['blogPosts', searchQuery, selectedCategory],
    queryFn: () => fetchBlogPosts(selectedCategory === 'all' ? undefined : selectedCategory, searchQuery),
  });

  // Filtrer les catégories pour n'afficher que celles qui ont des articles visibles
  const visibleCategories = React.useMemo(() => {
    if (!allCategories || !posts) return [];
    
    // Si on est dans une catégorie spécifique, on la garde même si elle est vide
    if (selectedCategory !== 'all') {
      const currentCat = allCategories.find(cat => cat.id === selectedCategory);
      return currentCat ? [currentCat] : [];
    }
    
    // Sinon, on filtre les catégories ayant des articles
    return allCategories.filter(category => 
      posts.some(post => 
        post.categories?.some(cat => cat.id === category.id)
      )
    );
  }, [allCategories, posts, selectedCategory]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault(); // Permet une soumission par la touche "Entrée"
  };

  const handleCategoryChange = (value: string) => {
    setBlogCategory(value);
  };

  return (
    <PublicLayout>
      <SEOMetaTags
        title="Blog - ECATHEBA"
        description="Découvrez nos articles théologiques, réflexions spirituelles et actualités de l'École Cathédrale de Théologie de Bafoussam."
        image="https://pgnkuxeckqnzkyfcrwzs.supabase.co/storage/v1/object/public/media/tenant/3/1754983837000-2gd5is5zflc.JPG"
        url="https://ecatheba.com/blog"
      />

      <div className="min-h-screen relative overflow-hidden">
        {/* Fond avec la nouvelle palette de couleurs SIS */}
        <div 
          className="fixed inset-0 z-0 bg-center bg-no-repeat bg-cover"
          style={{
            backgroundImage: tenant?.config?.bg ? `url(${tenant.config.bg})` : 'url(https://images.pexels.com/photos/326503/pexels-photo-326503.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2)',
            backgroundAttachment: 'fixed'
          }}
        >
          {blogTheme.overlay.enabled && (
            <div className="absolute inset-0 backdrop-blur-[2px]" style={{
              backgroundImage: blogTheme.overlay.useGradient
                ? `linear-gradient(${(
                    {
                      'to-b': 'to bottom',
                      'to-t': 'to top',
                      'to-r': 'to right',
                      'to-l': 'to left',
                      'to-tr': 'to top right',
                      'to-br': 'to bottom right',
                      'to-tl': 'to top left',
                      'to-bl': 'to bottom left',
                    } as Record<string, string>
                  )[blogTheme.overlay.orientation] || 'to bottom'},
                    ${hexToRgba(blogTheme.overlay.color1, blogTheme.overlay.opacity1)},
                    ${hexToRgba(blogTheme.overlay.color2, blogTheme.overlay.opacity2)},
                    ${hexToRgba(blogTheme.overlay.color3, blogTheme.overlay.opacity3)}
                  )`
                : undefined,
              backgroundColor: !blogTheme.overlay.useGradient
                ? hexToRgba(blogTheme.overlay.color1, blogTheme.overlay.opacity2)
                : undefined,
            }} />
          )}
        </div>

        {/* Particules flottantes avec couleurs et icônes personnalisables */}
        <div className="absolute inset-0 overflow-hidden z-10">
          <div className="absolute top-1/4 left-1/4 w-64 h-64 rounded-full blur-3xl animate-pulse" style={{ backgroundColor: hexToRgba(tenant?.config?.color1 || '#fe8d17', 0.08) }} />
          <div className="absolute top-3/4 right-1/4 w-96 h-96 rounded-full blur-3xl animate-pulse" style={{ backgroundColor: hexToRgba(tenant?.config?.color2 || '#008aef', 0.08), animationDelay: '2s' }} />
          <div className="absolute top-1/2 left-3/4 w-48 h-48 rounded-full blur-2xl animate-pulse" style={{ backgroundColor: hexToRgba(tenant?.config?.color1 || '#fe8d17', 0.15), animationDelay: '4s' }} />
        </div>
        
        {/* Éléments décoratifs géométriques avec couleurs personnalisables */}
        <div className="absolute top-20 right-10 opacity-15 z-10">
          <Crown className="w-16 h-16 animate-spin" style={{ color: hexToRgba(tenant?.config?.color1 || '#fe8d17', 0.15), animationDuration: '20s' }} />
        </div>
        <div className="absolute bottom-20 left-10 opacity-15 z-10">
          <Sparkles className="w-12 h-12 animate-bounce" style={{ color: hexToRgba(tenant?.config?.color2 || '#008aef', 0.15) }} />
        </div>
        <div className="absolute top-1/3 left-20 opacity-10 z-10">
          <Diamond className="w-8 h-8 animate-pulse" style={{ color: hexToRgba(tenant?.config?.color1 || '#fe8d17', 0.1), animationDelay: '1s' }} />
        </div>
        <div className="absolute bottom-1/3 right-20 opacity-10 z-10">
          <Star className="w-10 h-10 animate-twinkle" style={{ color: hexToRgba(tenant?.config?.color3 || '#ffffff', 0.1), animationDelay: '3s' }} />
        </div>

        {/* Contenu de la page */}
        <div className="relative z-10">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-12 md:pt-24 md:pb-20">
            {/* En-tête de la section - Style ECATHEBA */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, type: "spring", stiffness: 100 }}
              className="mb-8 py-16 text-center"
            >
              <motion.div 
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="inline-flex items-center bg-white/10 backdrop-blur-sm rounded-full px-8 py-4 mb-8 shadow-xl"
                style={{ 
                  borderColor: `${tenant?.config?.color1 || '#fe8d17'}4d`,
                  borderWidth: '1px'
                }}
              >
                <Newspaper className="w-5 h-5 mr-3 animate-pulse" style={{ color: tenant?.config?.color1 || '#fe8d17' }} />
                <span className="text-white" style={{ color: tenant?.config?.color3 || '#ffffff' }}>{blogHeaderConfig.badgeLabel}</span>
                <Sparkles className="w-4 h-4 ml-3 animate-bounce" style={{ color: tenant?.config?.color1 || '#fe8d17', animationDelay: '0.5s' }} />
              </motion.div>
              
              <motion.h1 
                initial={{ opacity: 0, y: 20, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 0.8, delay: 0.4, type: "spring", stiffness: 80 }}
                className="text-4xl md:text-6xl font-bold text-white mb-4 drop-shadow-lg"
              >
                {blogHeaderConfig.title.split(' ').slice(0, -1).join(' ')}{' '}
                <span style={{ color: tenant?.config?.color1 || '#fe8d17' }}>
                  {blogHeaderConfig.title.split(' ').slice(-1)[0]}
                </span>
              </motion.h1>
              
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.6 }}
                className="text-white/90 text-lg drop-shadow-md max-w-3xl mx-auto"
              >
                {blogHeaderConfig.line1}
                <br />
                <span style={{ color: tenant?.config?.color1 || '#fe8d17' }}>{blogHeaderConfig.line2}</span>.
              </motion.p>
            </motion.div>

            {/* Contenu principal */}
            <div className="max-w-7xl mx-auto">
              {/* Barre de recherche et filtres */}
              <motion.div 
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="mb-12 bg-white/5 border border-white/10 text-white backdrop-blur-sm shadow-xl rounded-2xl p-8"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
                  <form onSubmit={handleSearch} className="relative w-full">
                    <Search className="absolute left-4 top-1/2 transform -translate-y-1/2" size={20} style={{ color: hexToRgba(tenant?.config?.color3 || '#008aef', 0.6) }} />
                    <Input
                      placeholder="Rechercher un article, une réflexion..."
                      className="pl-12 pr-4 py-3 text-base bg-white/10 border border-white/20 focus:ring-2 focus:border-transparent outline-none transition-all duration-300 placeholder-white/50"
                      style={{
                        color: tenant?.config?.color3 || '#ffffff',
                        borderColor: hexToRgba(tenant?.config?.color11 || '#ffffff', 0.3),
                        '--tw-ring-color': tenant?.config?.color11 || '#ffffff'
                      } as React.CSSProperties}
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                  </form>
                  <div className="overflow-x-auto">
                    <div className="flex justify-start md:justify-end space-x-2 pb-1">
                      <Button
                        key="view-all"
                        variant={!selectedCategory ? 'default' : 'outline'}
                        onClick={() => handleCategoryChange('')}
                        className="whitespace-nowrap px-4 py-2 text-sm font-medium text-white hover:bg-white/10 transition-all duration-300"
                        style={{
                          backgroundColor: !selectedCategory 
                            ? `linear-gradient(to right, ${tenant?.config?.color2 || '#008aef'}, ${tenant?.config?.color3 || '#0066cc'})`
                            : `${tenant?.config?.color3 || '#fe8d17'}33`,
                          color: !selectedCategory 
                            ? tenant?.config?.color3 || '#ffffff'
                            : tenant?.config?.color3 || '#008aef',
                          borderColor: !selectedCategory 
                            ? 'transparent'
                            : `${tenant?.config?.color1 || '#fe8d17'}33`,
                          backgroundImage: !selectedCategory 
                            ? `linear-gradient(to right, ${tenant?.config?.color3 || '#008aef'}, ${hexToRgba(tenant?.config?.color3 || '#008aef', 0.8)})`
                            : undefined
                        }}
                      >
                        Tout voir
                      </Button>
                      {visibleCategories?.map((category) => (
                        <Button
                          key={category.id}
                          variant={selectedCategory === category.id ? 'default' : 'outline'}
                          onClick={() => handleCategoryChange(category.id)}
                          className="whitespace-nowrap px-4 py-2 text-sm font-medium border-white/20 text-white hover:bg-white/10 transition-all duration-300"
                          style={{
                            backgroundColor: selectedCategory === category.id 
                              ? `linear-gradient(to right, ${tenant?.config?.color3 || '#008aef'}, ${tenant?.config?.color3 || '#0066cc'})`
                              : `${tenant?.config?.color3 || '#fe8d17'}33`,
                            color: selectedCategory === category.id 
                              ? tenant?.config?.color3 || '#ffffff'
                              : tenant?.config?.color3 || '#008aef',
                            borderColor: selectedCategory === category.id 
                              ? 'transparent'
                              : `${tenant?.config?.color1 || '#fe8d17'}33`,
                            backgroundImage: selectedCategory === category.id 
                              ? `linear-gradient(to right, ${tenant?.config?.color3 || '#008aef'}, ${hexToRgba(tenant?.config?.color3 || '#008aef', 0.8)})`
                              : undefined
                          }}
                        >
                          {category.name}
                        </Button>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
          
              {/* Affichage des articles */}
              {(categoriesLoading || postsLoading) ? (
                <div className="flex justify-center items-center py-20 bg-white/5 backdrop-blur-sm"
                  style={{
                    borderColor: hexToRgba(tenant?.config?.color1 || '#fe8d17', 0.3)
                  }}
                >
                  <div className="relative">
                    <div className="w-16 h-16 border-4 rounded-full animate-spin"
                      style={{
                        borderColor: hexToRgba(tenant?.config?.color1 || '#fe8d17', 0.2),
                        borderTopColor: tenant?.config?.color1 || '#fe8d17'
                      }}
                    ></div>
                    <div className="absolute inset-0 w-16 h-16 border-4 border-transparent rounded-full animate-spin" 
                      style={{ 
                        borderTopColor: hexToRgba(tenant?.config?.color3 || '#008aef', 0.3),
                        animationDirection: 'reverse',
                        animationDuration: '1.5s'
                      }}
                    ></div>
                  </div>
                  <span className="ml-4 font-medium" style={{ color: tenant?.config?.color3 || '#008aef' }}>Chargement des articles...</span>
                </div>
              ) : posts?.length === 0 ? (
                <div className="text-center py-20 bg-white/5 backdrop-blur-sm rounded-lg border border-white/10">
                  <h2 className="text-2xl font-bold mb-4 text-white">Aucun article ne correspond à votre recherche</h2>
                  <p className="mb-6 text-white/80">
                    {searchQuery || selectedCategory !== 'all'
                      ? 'Essayez avec d\'autres mots-clés ou en changeant de catégorie.'
                      : "De nouveaux articles sont en cours de rédaction. Revenez bientôt."}
                  </p>
                  {(searchQuery || selectedCategory !== 'all') && (
                    <Button 
                      onClick={resetFilters} 
                      className="shadow-lg hover:shadow-xl transition-all duration-300 font-bold"
                      style={{
                        backgroundColor: tenant?.config?.color1 || '#fe8d17',
                        color: tenant?.config?.color3 || '#ffffff',
                        backgroundImage: `linear-gradient(to right, ${tenant?.config?.color1 || '#fe8d17'}, ${tenant?.config?.color1 || '#e47c15'})`,
                        borderColor: hexToRgba(tenant?.config?.color1 || '#fe8d17', 0.4),
                        boxShadow: `0 4px 15px ${hexToRgba(tenant?.config?.color1 || '#fe8d17', 0.4)}`
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundImage = `linear-gradient(to right, ${tenant?.config?.color1 || '#e07a10'}, ${tenant?.config?.color1 || '#fe8d17'})`;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundImage = `linear-gradient(to right, ${tenant?.config?.color1 || '#fe8d17'}, ${tenant?.config?.color1 || '#e47c15'})`;
                      }}
                    >
                      Réinitialiser les filtres
                    </Button>
                  )}
                </div>
              ) : (
                <motion.div 
                  className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
                  variants={containerVariants}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                >
                  {posts?.map((post) => (
                    <motion.article
                      key={post.id}
                      variants={cardVariants}
                      className="group relative block bg-white/5 backdrop-blur-sm overflow-hidden rounded-lg border text-white transition-all duration-300 hover:shadow-2xl hover:scale-[1.02]"
                      style={{
                        borderColor: 'rgba(255, 255, 255, 0.1)',
                        '--tw-border-color': 'rgba(255, 255, 255, 0.1)',
                        '--tw-hover-border-color': tenant?.config?.color1 || '#fe8d17'
                      } as React.CSSProperties}
                    >
                      {post.featured_image_url && (
                        <div className="relative h-52 overflow-hidden">
                          <Link to={`/blog/${post.slug}`} className="block w-full h-full">
                            <img
                              src={post.featured_image_url}
                              alt={post.title}
                              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t to-transparent" style={{ backgroundImage: `linear-gradient(to top, ${hexToRgba(tenant?.config?.color1 || '#008aef', 0.5)}, transparent)` }}></div>
                          </Link>
                        </div>
                      )}
                      <div className="p-6 flex flex-col flex-grow">
                        <h2 className="text-xl font-bold mb-3" style={{ color: tenant?.config?.color3 || '#008aef' }}>
                          <Link to={`/blog/${post.slug}`} className="text-white transition-colors duration-200"
                            style={{
                              color: tenant?.config?.color3 || '#008aef',
                              '--tw-hover-color': tenant?.config?.color1 || '#fe8d17'
                            } as React.CSSProperties}
                          >
                            {post.title}
                          </Link>
                        </h2>
                        <div className="flex items-center text-white/60" style={{ color: hexToRgba(tenant?.config?.color2 || '#008aef', 0.6) }}>
                          <Calendar className="mr-2 h-4 w-4" />
                          <time dateTime={post.created_at}>
                            {format(new Date(post.created_at), 'dd MMMM yyyy', { locale: fr })}
                          </time>
                        </div>
                        <p className="text-white/80 mb-4 line-clamp-3 flex-grow" style={{ color: hexToRgba(tenant?.config?.color2 || '#008aef', 0.8) }}>{post.excerpt}</p>
                        <div className="mt-auto pt-4 border-t" style={{ borderColor: hexToRgba(tenant?.config?.color3 || '#fe8d17', 0.2) }}>
                          {post.categories && post.categories.length > 0 && (
                            <div className="flex flex-wrap gap-2 mb-4">
                              {post.categories.map((category) => (
                                <Badge key={category.id} variant="secondary" className="backdrop-blur-sm"
                                  style={{
                                    backgroundColor: hexToRgba(tenant?.config?.color3 || '#fe8d17', 0.2), 
                                    color: tenant?.config?.color3 || '#008aef',
                                    borderColor: hexToRgba(tenant?.config?.color3 || '#fe8d17', 0.3)
                                  }}
                                >
                                  {category.name}
                                </Badge>
                              ))}
                            </div>
                          )}
                          <Button
                            asChild
                            className="group relative overflow-hidden font-semibold w-full px-6 py-3 rounded-lg transition-all duration-300 hover:shadow-lg hover:scale-[1.02] border"
                            style={{
                              backgroundColor: hexToRgba(tenant?.config?.color1 || '#fe8d17', 0.9),
                              color: tenant?.config?.color3 || '#fe8d17',
                              borderColor: hexToRgba(tenant?.config?.color1 || '#fe8d17', 0.3)
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = tenant?.config?.color1 || '#fe8d17';
                              e.currentTarget.style.borderColor = tenant?.config?.color1 || '#fe8d17';
                              e.currentTarget.style.color = tenant?.config?.color3 || '#ffffff';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = hexToRgba(tenant?.config?.color1 || '#fe8d17', 0.1);
                              e.currentTarget.style.borderColor = hexToRgba(tenant?.config?.color1 || '#fe8d17', 0.3);
                              e.currentTarget.style.color = tenant?.config?.color1 || '#fe8d17';
                            }}
                          >
                            <Link to={`/blog/${post.slug}`} className="flex items-center justify-center relative z-10">
                              <span className="mr-2">Lire l'article</span>
                              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                            </Link>
                          </Button>
                        </div>
                      </div>
                    </motion.article>
                  ))}
                </motion.div>
              )}
            </div>
          </div>
        </div>
      </div>
    </PublicLayout>
  );
};

export default BlogHome;